import React, { useState } from 'react'
import { ChevronDown, Folder, RefreshCw } from 'lucide-react'
import { Project } from '../../types'
import { useAuth } from '../../contexts/AuthContext'
import { canViewProjectBudget } from '../../utils/projectUtils'

interface ProjectSelectorProps {
  projects: Project[]
  selectedProject: Project | null
  recentFavoriteProjects: Project[]
  onSelectProject: (project: Project | null) => void
  onReloadProjects?: () => Promise<void>
  disabled?: boolean
  className?: string
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedProject,
  recentFavoriteProjects,
  onSelectProject,
  onReloadProjects,
  disabled = false,
  className = ''
}) => {
  const [isReloading, setIsReloading] = useState(false)
  const { user } = useAuth()

  const handleReloadProjects = async () => {
    if (!onReloadProjects || isReloading) return
    
    try {
      setIsReloading(true)
      await onReloadProjects()
    } catch (error) {
      console.error('重新載入專案失敗:', error)
    } finally {
      setIsReloading(false)
    }
  }

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-secondary-700">
          選擇專案
        </label>
        
        {/* 重新載入按鈕 */}
        {onReloadProjects && (
          <button
            onClick={handleReloadProjects}
            disabled={disabled || isReloading}
            className={`
              flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium
              transition-all duration-200
              ${disabled || isReloading
                ? 'text-secondary-400 cursor-not-allowed'
                : 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
              }
            `}
            title="重新載入專案列表"
          >
            <RefreshCw className={`w-3 h-3 ${isReloading ? 'animate-spin' : ''}`} />
            <span>{isReloading ? '載入中...' : '重新載入'}</span>
          </button>
        )}
      </div>
      
      <div className="relative">
        <select
          value={selectedProject?.id || ''}
          onChange={(e) => {
            const project = projects.find(p => p.id === e.target.value) || null
            onSelectProject(project)
          }}
          disabled={disabled}
          className={`
            w-full px-4 py-3 pr-10
            bg-white border border-secondary-300 rounded-lg
            text-secondary-900 placeholder-secondary-500
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
            disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
            transition-colors duration-200
          `}
        >
          <option value="">請選擇專案</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        
        {/* 自定義下拉箭頭 */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className="w-5 h-5 text-secondary-400" />
        </div>
      </div>
      
      {/* 最近常用專案 - 只在未選擇專案時顯示 */}
      {!selectedProject && recentFavoriteProjects.length > 0 && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          {recentFavoriteProjects.map((project) => (
            <button
              key={project.id}
              onClick={() => onSelectProject(project)}
              disabled={disabled}
              className={`
                flex items-center space-x-2 p-2 rounded-lg border
                text-left transition-colors duration-200
                ${disabled 
                  ? 'bg-secondary-50 text-secondary-400 cursor-not-allowed border-secondary-200' 
                  : 'bg-white hover:bg-secondary-50 border-secondary-200 hover:border-primary-300'
                }
              `}
            >
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.color }}
              />
              <span className="text-sm font-medium text-secondary-800 truncate">
                {project.name}
              </span>
            </button>
          ))}
        </div>
      )}
      
      {/* 選中的專案資訊 */}
      {selectedProject && (
        <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: selectedProject.color }}
            />
            <Folder className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-800">
              {selectedProject.name}
            </span>
          </div>
          
          {selectedProject.description && (
            <p className="mt-1 text-xs text-primary-600">
              {selectedProject.description}
            </p>
          )}
          
          {/* 專案預算資訊 - 只有有權限的用戶才能看到 */}
          {selectedProject.budget && canViewProjectBudget(user, selectedProject) && (
            <div className="mt-2 text-xs text-primary-600">
              <span className="font-medium">預算:</span> 
              {selectedProject.budget.currency} {selectedProject.budget.amount.toLocaleString()}
              {selectedProject.budget.type === 'MONTHLY' ? ' / 月' : ' (總計)'}
            </div>
          )}
        </div>
      )}
      
      {/* 無專案提示 */}
      {projects.length === 0 && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Folder className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              目前沒有可用的專案，請先建立專案
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectSelector
